import { Injectable } from '@angular/core';
import Feature from 'ol/Feature';
import OlMap from 'ol/Map';
import Point from 'ol/geom/Point';
import VectorLayer from 'ol/layer/Vector';
import type { Pixel } from 'ol/pixel';
import VectorSource from 'ol/source/Vector';
import { Circle as CircleStyle, Fill, Icon, Stroke, Style } from 'ol/style';

import { lonLatToRdCoordinate, registerDutchRdProjection } from '../map/observer-map.helpers';
import { DetectedDroneViewModel, droneConfidenceToRadius } from '../models/detected-drone.model';

@Injectable({
  providedIn: 'root',
})
export class DetectedDroneMapLayerService {
  private static readonly iconSource = '/favicon.svg?v=202606030950';
  private readonly source = new VectorSource<Feature<Point>>();
  private readonly layer = new VectorLayer({
    source: this.source,
    properties: {
      name: 'DetectedDronesLayer',
    },
  });
  private readonly featuresById = new Map<string, Feature<Point>>();
  private readonly dronesById = new Map<string, DetectedDroneViewModel>();
  private map: OlMap | null = null;
  private selectedDroneId: string | null = null;

  attachToMap(map: OlMap): void {
    registerDutchRdProjection();

    if (this.map === map) {
      return;
    }

    this.detachFromMap();
    this.map = map;
    map.addLayer(this.layer);
  }

  detachFromMap(): void {
    if (!this.map) {
      return;
    }

    this.map.removeLayer(this.layer);
    this.map = null;
  }

  setVisible(isVisible: boolean): void {
    this.layer.setVisible(isVisible);
  }

  setSelectedDroneId(droneId: string | null): void {
    if (this.selectedDroneId === droneId) {
      return;
    }

    this.selectedDroneId = droneId;
    this.refreshFeatureStyles();
  }

  updateDrones(drones: DetectedDroneViewModel[]): void {
    registerDutchRdProjection();

    const freshDrones = drones.filter((drone) => !drone.isStale);
    const incomingIds = new Set(freshDrones.map((drone) => drone.droneId));

    for (const drone of freshDrones) {
      this.dronesById.set(drone.droneId, drone);
      const feature = this.featuresById.get(drone.droneId) ?? this.createFeature(drone);
      this.updateFeature(feature, drone);
    }

    for (const [id, feature] of this.featuresById) {
      if (!incomingIds.has(id)) {
        this.source.removeFeature(feature);
        this.featuresById.delete(id);
        this.dronesById.delete(id);
      }
    }
  }

  clear(): void {
    this.source.clear();
    this.featuresById.clear();
    this.dronesById.clear();
  }

  getDroneIdAtPixel(map: OlMap, pixel: Pixel): string | null {
    const droneId = map.forEachFeatureAtPixel(
      pixel,
      (feature) => {
        const value = feature.get('droneId');

        return typeof value === 'string' ? value : null;
      },
      {
        hitTolerance: 8,
        layerFilter: (layer) => layer === this.layer,
      },
    );

    return typeof droneId === 'string' ? droneId : null;
  }

  private createFeature(drone: DetectedDroneViewModel): Feature<Point> {
    const feature = new Feature<Point>();
    feature.setId(drone.droneId);
    this.featuresById.set(drone.droneId, feature);
    this.source.addFeature(feature);

    return feature;
  }

  private updateFeature(feature: Feature<Point>, drone: DetectedDroneViewModel): void {
    feature.setGeometry(new Point(lonLatToRdCoordinate([drone.longitude, drone.latitude])));
    feature.setProperties({
      droneId: drone.droneId,
      confidence: drone.confidence,
      observerCount: drone.observerCount,
      averageRayMissDistanceMeters: drone.averageRayMissDistanceMeters,
      sourceRayIds: drone.sourceRayIds,
      sourceDeviceIds: drone.sourceDeviceIds,
      sourceObserverIds: drone.sourceObserverIds,
      firstSeenAtUtc: drone.firstSeenAtUtc,
      lastSeenAtUtc: drone.lastSeenAtUtc,
    });
    feature.setStyle(this.createStyle(drone));
  }

  private createStyle(drone: DetectedDroneViewModel): Style[] {
    const iconSize = droneConfidenceToIconSize(drone.confidence);
    const iconStyle = new Style({
      image: new Icon({
        src: DetectedDroneMapLayerService.iconSource,
        anchor: [0.5, 0.5],
        anchorXUnits: 'fraction',
        anchorYUnits: 'fraction',
        width: iconSize,
        height: iconSize,
      }),
    });

    if (drone.droneId !== this.selectedDroneId) {
      return [iconStyle];
    }

    return [
      new Style({
        image: new CircleStyle({
          radius: Math.round(iconSize / 2) + 8,
          fill: new Fill({ color: 'rgb(255 255 255 / 38%)' }),
          stroke: new Stroke({
            color: '#2563eb',
            width: 3,
          }),
        }),
      }),
      iconStyle,
    ];
  }

  private refreshFeatureStyles(): void {
    for (const [droneId, feature] of this.featuresById) {
      const drone = this.dronesById.get(droneId);

      if (drone) {
        feature.setStyle(this.createStyle(drone));
      }
    }
  }
}

function droneConfidenceToIconSize(confidence: number): number {
  return droneConfidenceToRadius(confidence) * 2.6;
}
